import { useState } from 'react';
import AppWizard from 'components/templates/AppWizard';
import { showErrorToast, showSuccessToast } from 'features/toastSlice';
import { useNavigate, useParams } from 'react-router';
import { useDispatch } from 'react-redux';
import { admincreateMember, adminSignupMember } from 'api/portal';
import { isCpf, validateCpf, validateEmail } from 'utils/validation';
import { unformatCpf } from 'utils/format';
import { useTranslation } from 'react-i18next';
import { useFetchOrganizationAdmin } from 'hooks/useFetchOrganizationAdmin';
import Fade from 'components/Fade';
import PersonalAndCorporateInfo from './steps/PersonalAndCorporateInfo';
import DefinePassword from './steps/DefinePassword';

const MAP_FIELDS = {
  document: 'document',
  first_name: 'firstName',
  last_name: 'lastName',
  birth_date: 'birthYear',
  doctor_document: 'doctorDocument',
  email: 'email',
  phone_number: 'phoneNumber',
  roles: 'roles',
  units: 'units',
  notification_language: 'notificationLanguage',
};

// TODO - treat errors better
function Create() {
  const [personalData, setPersonalData] = useState({
    document: '',
    firstName: '',
    lastName: '',
    email: '',
    birthYear: '',
    notificationLanguage: 'pt-br',
  });
  const [corporateData, setCorporateData] = useState({
    email: '',
    phoneNumber: '',
    roles: [],
    units: [],
  });

  const [organization, setOrganization] = useState({});

  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState(new Map());
  const [errorsMember, setErrorsMember] = useState(new Map());
  const [step, setStep] = useState(0);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const params = useParams();
  const { orgId } = params;

  const { t } = useTranslation();

  useFetchOrganizationAdmin({
    orgId,
    onSuccess: (data) => setOrganization(data),
    onFailure: () => dispatch(showErrorToast('messages.load-failure')),
  });

  const STEPS = [
    t('organization.organization-members-create.wizard-personal-info'),
    t('organization.organization-members-create.wizard-define-password'),
  ];

  const HOME = t('organization.breadcrumbs.home');
  const MANAGE_ORGANIZATION = t('organization.breadcrumbs.manage-organization');
  const INVALID_USERNAME_ERROR = t('messages.invalid-document');
  const FILL_REQUIRED = t('messages.fill-required');
  const SELECT_AT_LEAST_ONE = t('messages.select-at-least-one');
  const ADD_MEMBER = organization
    ? t('braincare.organization-members.add-member', { organization: organization.name })
    : t('braincare.organization-members.add-member-default');

  const onFillMemberInfo = (data) => {
    const {
      userId,
      document,
      firstName,
      lastName,
      birthYear,
      notificationLanguage,
      isDocumentEmail,
      email,
      phoneNumber,
      roles,
      units,
    } = data;

    const personalErrors = new Map();
    const corporateErrors = new Map();

    if (!userId) {
      const requiredPersonalFields = {
        firstName,
        lastName,
        document,
        notificationLanguage,
      };

      Object.entries(requiredPersonalFields).forEach(([key, value]) => {
        if (!value) {
          personalErrors.set(key, FILL_REQUIRED);
        }
      });
    }

    if (document !== '' && (isCpf(document) ? !validateCpf(document) : !validateEmail(document))) {
      personalErrors.set('document', INVALID_USERNAME_ERROR);
    }

    const requiredCorporateFields = {
      email,
      roles,
    };

    Object.entries(requiredCorporateFields).forEach(([key, value]) => {
      if (key === 'roles' && value.length <= 0) {
        corporateErrors.set(key, SELECT_AT_LEAST_ONE);
      } else if (!value) {
        corporateErrors.set(key, FILL_REQUIRED);
      }
    });

    setErrors(personalErrors);
    setErrorsMember(corporateErrors);

    if (personalErrors.size || corporateErrors.size) {
      dispatch(showErrorToast('messages.fix-fields'));
      return;
    }

    setPersonalData({
      userId,
      document,
      firstName,
      lastName,
      birthYear,
      notificationLanguage,
      isDocumentEmail,
    });
    setCorporateData({ email, phoneNumber, roles, units });
    setStep(1);
  };

  const requestCreateMember = (userId, passwordData) => {
    const { email, phoneNumber, roles, units } = corporateData;
    const { password, emailNotification, forcePasswordChange } = passwordData;

    admincreateMember(orgId, {
      user: userId,
      email,
      phone_number: phoneNumber,
      roles,
      units,
      password,
      mail_again: emailNotification,
      force_password_change: forcePasswordChange,
    })
      .then(() => {
        setIsSaving(false);
        dispatch(showSuccessToast('organization.organization-members-create.save-member-success'));
        navigate(`/braincare/organizations/${orgId}/members`);
      })
      .catch((error) => {
        setIsSaving(false);

        const { response } = error;
        const { data } = response;

        const errors = new Map();

        Object.entries(data).forEach(([key, value]) => {
          errors.set(MAP_FIELDS[key], value[0]);
        });

        setErrorsMember(errors);

        if (data.non_field_errors) {
          dispatch(showErrorToast('organization.organization-members-create.message-member-exists'));
          return;
        }

        dispatch(showErrorToast('organization.organization-members-create.save-member-failure'));
      });
  };

  const requestCreateUser = (personalData, passwordData) => {
    const { document, firstName, lastName, birthYear, notificationLanguage } = personalData;

    adminSignupMember({
      document: isCpf(document) ? unformatCpf(document) : document,
      first_name: firstName,
      last_name: lastName,
      ...(birthYear && { birth_date: `${birthYear}-01-01` }),
      has_read_privacy_policy: false,
      has_read_terms_and_conditions: false,
      accept_terms: false,
      notification_language: notificationLanguage,
    })
      .then((response) => {
        const { data } = response;

        requestCreateMember(data.id, passwordData);
      })
      .catch((error) => {
        const { response } = error;
        const { data } = response;

        const errors = new Map();

        Object.entries(data).forEach(([key, value]) => {
          errors.set(MAP_FIELDS[key], value[0]);
        });

        setIsSaving(false);
        setErrors(errors);
        dispatch(showErrorToast('organization.organization-members-create.save-info-failure'));
      });
  };

  const onFillPassword = (data) => {
    const { userId } = personalData;

    setIsSaving(true);

    if (userId !== null) {
      requestCreateMember(userId, data);
    } else {
      requestCreateUser(personalData, data);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <PersonalAndCorporateInfo
            errors={errors}
            errorsMember={errorsMember}
            initialData={{
              ...personalData,
              ...corporateData,
              isDocumentEmail: personalData.isDocumentEmail,
            }}
            onSave={onFillMemberInfo}
          />
        );
      case 1:
        return (
          <DefinePassword
            isSaving={isSaving}
            memberEmail={corporateData.email}
            onPreviousStep={() => setStep(0)}
            onSave={onFillPassword}
          />
        );
      default:
        return null;
    }
  };

  const breadcrumbs = [
    {
      path: '/',
      title: HOME,
    },
    {
      path: `/braincare/organizations/${orgId}/members`,
      title: MANAGE_ORGANIZATION,
    },
    {
      path: `/braincare/organizations/${orgId}/create`,
      title: ADD_MEMBER,
      isActive: true,
    },
  ];

  return (
    <AppWizard title={ADD_MEMBER} breadcrumbs={breadcrumbs} steps={STEPS} currentStep={step}>
      <Fade>{renderStep()}</Fade>
    </AppWizard>
  );
}

export default Create;
